import { useState, useEffect, useCallback } from 'react';
import { createFileRoute, Outlet, useRouter, useRouterState } from '@tanstack/react-router';
import { ArrowLeft, AlertCircle, RefreshCw } from 'lucide-react';
import { usePermissionScope } from '@/hooks/usePermissions';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { ContactProjectsPane } from '@/components/organisms/contacts/detail/ContactProjectsPane';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { RoutePermissionDenied } from '@/components/molecules/RoutePermissionGuard';
import { Card, CardContent } from '@/components/atoms/card';
import { Skeleton } from '@/components/atoms/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/atoms/dialog';
import { useContact } from '@/hooks/contacts/useContact';
import { useSeedContactPrefill } from '@/lib/contacts/contact-prefill-cache';
import { useDeleteContact } from '@/hooks/contacts/useDeleteContact';
import { useContactSummary } from '@/hooks/contacts/useContactSummary';
import {
  useContactEstimatesSection,
  useContactInvoicesSection,
} from '@/hooks/contacts/useContactSections';
import { DetailPageLayout } from '@/components/layouts';
import { ContactDetailHeader } from '@/components/organisms/contacts/ContactDetailHeader';
import { ContactSearchSwitcher } from '@/components/organisms/contacts/detail/ContactSearchSwitcher';
import { ContactInfoSidebar } from '@/components/organisms/contacts/detail/ContactInfoSidebar';
import { ContactDetailMobile } from '@/components/organisms/contacts/detail/ContactDetailMobile';
import { ContactDeletedBanner } from '@/components/organisms/contacts/detail/ContactDeletedBanner';
import {
  ContactPaymentMethodsSection,
  type ContactPaymentsQuickAction,
} from '@/components/organisms/contacts/ContactPaymentMethodsSection';
import {
  ContactSectionNav,
  CONTACT_SECTION_IDS,
  type ContactSectionId,
} from '@/components/organisms/contacts/detail/ContactSectionNav';
import { ContactCustomerSummary } from '@/components/organisms/contacts/detail/ContactCustomerSummary';
import { ContactBillToSection } from '@/components/organisms/contacts/ContactBillToSection';
import { ContactLocationsPane } from '@/components/organisms/contacts/detail/ContactLocationsPane';
import { ContactTaxExemptionPane } from '@/components/organisms/contacts/detail/ContactTaxExemptionPane';
import { ContactWorkTable } from '@/components/organisms/contacts/detail/ContactWorkTable';
import { ContactBillingTable } from '@/components/organisms/contacts/detail/ContactBillingTable';
import { ContactPaymentsSection } from '@/components/organisms/contacts/ContactPaymentsSection';
import { ContactFinancialActivitySection } from '@/components/organisms/contacts/ContactFinancialActivitySection';
import { ContactReceiptsSection } from '@/components/organisms/contacts/ContactReceiptsSection';
import { ContactMembershipsSection } from '@/components/organisms/contacts/ContactMembershipsSection';
import { ContactCommunicationTimeline } from '@/components/organisms/contacts/detail/ContactCommunicationLog';
import { ContactCommunicationDialog } from '@/components/organisms/contacts/detail/ContactCommunicationDialog';
import { ContactNotesDocsPane } from '@/components/organisms/contacts/detail/ContactNotesDocsPane';
import { ContactEquipmentPane } from '@/components/organisms/contacts/detail/ContactEquipmentPane';
import { ContactMediaPane } from '@/components/organisms/contacts/detail/ContactMediaPane';
import { useBillTo } from '@/hooks/contacts/useBillTo';
import { apiPost, operationsClient } from '@/lib/api';
import { membershipEndpoints, recurringJobEndpoints } from '@/lib/api/endpoints';
import { getContactTagIds, normalizeContactAssignedTags } from '@/lib/contacts/contactTags';
import { contactKeys } from '@/lib/queryKeys';
import type { BillTo, Contact } from '@/types/contact';
import { DEFAULT_CREDIT_MEMO_LIST_FILTERS } from '@/components/organisms/credit-memos/credit-memo-list-filters';

export const Route = createFileRoute('/_dashboard/dashboard/contacts/$contactId')({
  // `tab` persists the active section in the URL (bookmarkable, survives reload).
  // The parent `contacts` route validates the list filters (q/type/...), which
  // merge with these — so they are preserved when switching sections.
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    tab?: ContactSectionId;
    paymentAction?: 'add-card' | 'request-card' | 'collect-payment';
    paymentActionToken?: string;
    returnTo?: string;
    retentionEntity?: 'membership' | 'job';
    retentionId?: string;
    retentionOfferType?: string;
    retentionReasonCategory?: string;
    retentionReasonText?: string;
  } => ({
    tab: CONTACT_SECTION_IDS.includes(search.tab as ContactSectionId)
      ? (search.tab as ContactSectionId)
      : 'summary',
    paymentAction:
      search.paymentAction === 'add-card' ||
      search.paymentAction === 'request-card' ||
      search.paymentAction === 'collect-payment'
        ? search.paymentAction
        : undefined,
    paymentActionToken:
      typeof search.paymentActionToken === 'string' ? search.paymentActionToken : undefined,
    returnTo:
      typeof search.returnTo === 'string' && isJobDetailReturnTo(search.returnTo)
        ? search.returnTo
        : undefined,
    retentionEntity:
      search.retentionEntity === 'membership' || search.retentionEntity === 'job'
        ? search.retentionEntity
        : undefined,
    retentionId: typeof search.retentionId === 'string' ? search.retentionId : undefined,
    retentionOfferType:
      typeof search.retentionOfferType === 'string' ? search.retentionOfferType : undefined,
    retentionReasonCategory:
      typeof search.retentionReasonCategory === 'string'
        ? search.retentionReasonCategory
        : undefined,
    retentionReasonText:
      typeof search.retentionReasonText === 'string' ? search.retentionReasonText : undefined,
  }),
  component: ContactDetailPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isJobDetailReturnTo(value: string): boolean {
  const [pathname] = value.split(/[?#]/, 1);
  return /^\/dashboard\/jobs\/[^/]+$/.test(pathname ?? '');
}

function parseJobDetailReturnTo(
  value?: string,
): { id: string; search: Record<string, string> } | null {
  if (!value || !isJobDetailReturnTo(value)) return null;
  const [path = '', query = ''] = value.split('?');
  const match = /^\/dashboard\/jobs\/([^/]+)$/.exec(path);
  const id = match?.[1] ? decodeURIComponent(match[1]) : null;
  if (!id) return null;
  return { id, search: Object.fromEntries(new URLSearchParams(query)) };
}

function getContactDisplayName(contact: Contact): string {
  return (
    contact.displayName ||
    [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() ||
    contact.companyName ||
    'Contact'
  );
}

function getContactInitials(contact: Contact): string {
  const parts = getContactDisplayName(contact).split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? '').join('') || '?';
}

type ComposeChannel = 'SMS' | 'EMAIL';

type ComposeState = {
  open: boolean;
  channel: ComposeChannel;
  to: string;
};

// ─── Page ─────────────────────────────────────────────────────────────────────

function ContactDetailPage() {
  const contactViewScope = usePermissionScope('contacts:view');
  const { contactId } = Route.useParams();
  const router = useRouter();
  const { location } = useRouterState();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();

  const isDesktop = useIsDesktop();
  // includeDeleted: this page is the one place an operator should be able to
  // open a soft-deleted contact (to restore it). Other useContact consumers
  // keep the default 404-on-deleted behavior.
  const query = useContact(contactId, { includeDeleted: true });
  // NOT-579 — hand the loaded contact to a follow-on estimate/job create form so
  // its customer section hydrates instantly instead of refetching.
  useSeedContactPrefill(query.data);
  const billToQuery = useBillTo(contactId);
  const summaryQuery = useContactSummary(contactId);
  const estimatesCountQuery = useContactEstimatesSection(contactId, { page: 1, limit: 1 });
  const invoicesCountQuery = useContactInvoicesSection(contactId, { page: 1, limit: 1 });
  const deleteMutation = useDeleteContact();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [compose, setCompose] = useState<ComposeState>({
    open: false,
    channel: 'SMS',
    to: '',
  });
  const activeSection = search.tab ?? 'summary';
  const setActiveSection = useCallback(
    (id: ContactSectionId) =>
      void navigate({ search: (prev) => ({ ...prev, tab: id }), replace: true }),
    [navigate],
  );
  const [paymentsAction, setPaymentsAction] = useState<ContactPaymentsQuickAction | null>(null);
  const [paymentsActionToken, setPaymentsActionToken] = useState(0);
  const [tagIds, setTagIds] = useState<string[]>([]);

  async function handleDelete() {
    try {
      await deleteMutation.mutateAsync(contactId);
      setDeleteOpen(false);
      void router.navigate({ to: '/dashboard/contacts' });
    } catch {
      // error toast handled by the mutation hook
    }
  }

  const contact = query.data;
  const primaryEmail = contact?.primaryEmail;
  const primaryPhone = contact?.primaryPhone;
  const paymentActionFromSearch = search.paymentAction ?? null;
  const paymentActionTokenFromSearch = search.paymentActionToken ?? '';

  async function completePaymentRetentionOffer() {
    if (
      !search.retentionEntity ||
      !search.retentionId ||
      search.retentionOfferType !== 'UPDATE_PAYMENT_METHOD'
    ) {
      return;
    }
    const endpoint =
      search.retentionEntity === 'membership'
        ? membershipEndpoints.saveOffer(search.retentionId)
        : recurringJobEndpoints.saveOffer(search.retentionId);
    try {
      await apiPost(operationsClient, endpoint, {
        offerType: search.retentionOfferType,
        reasonCategory: search.retentionReasonCategory,
        reasonText: search.retentionReasonText,
        accepted: true,
        outcome: 'SAVED',
      });
      toast.success('Cancellation avoided - payment method updated');
    } catch {
      toast.warning(
        'The payment method was saved, but the retention result could not be recorded.',
      );
    }
    void navigate({
      search: (previous) => ({
        ...previous,
        retentionEntity: undefined,
        retentionId: undefined,
        retentionOfferType: undefined,
        retentionReasonCategory: undefined,
        retentionReasonText: undefined,
        paymentAction: undefined,
        paymentActionToken: undefined,
      }),
      replace: true,
    });
  }

  const openEmailCompose = useCallback(
    (to?: string | null) => {
      setCompose({ open: true, channel: 'EMAIL', to: to ?? primaryEmail ?? '' });
    },
    [primaryEmail],
  );

  const openSmsCompose = useCallback(
    (to?: string | null) => {
      setCompose({ open: true, channel: 'SMS', to: to ?? primaryPhone ?? '' });
    },
    [primaryPhone],
  );

  useEffect(() => {
    setTagIds(getContactTagIds(contact));
  }, [contact]);

  useEffect(() => {
    if (!paymentActionFromSearch) return;

    setActiveSection('payments');
    setPaymentsAction(paymentActionFromSearch);
    setPaymentsActionToken((current) => current + 1);
  }, [paymentActionFromSearch, paymentActionTokenFromSearch, setActiveSection]);

  // The API deliberately makes hidden and missing customers indistinguishable.
  // Explain the scoped user's access boundary without inferring record existence.
  const hasScopedContactAccess = contactViewScope === 'OWN' || contactViewScope === 'TEAM';
  if (
    query.isError &&
    (query.error?.status === 403 || (query.error?.status === 404 && hasScopedContactAccess))
  ) {
    return (
      <RoutePermissionDenied
        title="You don't have access to this customer"
        message="Contact your administrator if you need access."
        action={
          <Button onClick={() => void router.navigate({ to: '/dashboard/contacts' })}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Contacts
          </Button>
        }
      />
    );
  }

  const isExactRoute = location.pathname === `/dashboard/contacts/${contactId}`;
  if (!isExactRoute) {
    return <Outlet />;
  }

  // ── Error: 404 ──
  if (query.isError && query.error?.status === 404) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <AlertCircle className="h-12 w-12 text-muted-foreground" />
        <div>
          <p className="text-lg font-semibold">Contact not found</p>
          <p className="text-sm text-muted-foreground">
            The contact you're looking for doesn't exist or has been deleted.
          </p>
        </div>
        <Button onClick={() => void router.navigate({ to: '/dashboard/contacts' })}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Contacts
        </Button>
      </div>
    );
  }

  // ── Error: generic ──
  if (query.isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">
          {query.error?.message ?? 'Failed to load contact'}
        </p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          <RefreshCw className="mr-2 h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }
  const billToEntries: BillTo[] = Array.isArray(billToQuery.data) ? billToQuery.data : [];
  const displayName = contact ? getContactDisplayName(contact) : 'Contact';
  const initials = contact ? getContactInitials(contact) : '?';
  const defaultBillTo = billToEntries.find((entry) => entry.isDefault) ?? billToEntries[0] ?? null;
  const billToSummary = billToQuery.isLoading
    ? 'Loading…'
    : (defaultBillTo?.label ?? (billToEntries.length ? 'Configured' : 'Self'));

  async function handleTagIdsChange(nextTagIds: string[]) {
    setTagIds(nextTagIds);
    if (!contact) return;

    try {
      await operationsClient.patch(`/contacts/${contact.id}`, {
        tagIds: nextTagIds,
      });
      queryClient.setQueryData<Contact>(contactKeys.detail(contact.id), (current) =>
        current
          ? {
              ...current,
              tags: normalizeContactAssignedTags(undefined, nextTagIds),
              tagIds: nextTagIds,
            }
          : current,
      );
      await queryClient.invalidateQueries({ queryKey: contactKeys.detail(contact.id) });
      await queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
    } catch {
      toast.error('Failed to update tags');
      setTagIds(getContactTagIds(contact));
    }
  }

  function openPaymentsAction(action: ContactPaymentsQuickAction) {
    setActiveSection('payments');
    setPaymentsAction(action);
    setPaymentsActionToken((current) => current + 1);
  }

  const goBack = () => {
    const returnTarget = parseJobDetailReturnTo(search.returnTo);
    if (returnTarget) {
      void router.navigate({
        to: '/dashboard/jobs/$id',
        params: { id: returnTarget.id },
        search: returnTarget.search,
      });
      return;
    }

    void router.navigate({
      to: '/dashboard/contacts',
      search: location.search as Record<string, unknown>,
    });
  };

  const header = (
    <>
      <div className="flex h-12 items-center gap-3 border-b px-4">
        <button
          type="button"
          onClick={goBack}
          className="flex shrink-0 items-center gap-1.5 text-sm font-semibold hover:opacity-80"
        >
          <ArrowLeft className="size-4" />
          Contacts
        </button>
        <div className="ml-auto w-full max-w-xs">
          <ContactSearchSwitcher />
        </div>
      </div>
      <ContactDetailHeader
        contact={contact}
        isLoading={query.isLoading}
        displayName={displayName}
        initials={initials}
        billToSummary={billToSummary}
        onBack={goBack}
        onEdit={() =>
          void router.navigate({
            to: '/dashboard/contacts/$contactId/edit',
            params: { contactId },
          })
        }
        onDelete={() => setDeleteOpen(true)}
        onEmailCompose={() => openEmailCompose()}
        onOpenBillTo={() => setActiveSection('bill-to')}
        onOpenPaymentsAction={openPaymentsAction}
      />
      {contact?.isDeleted ? <ContactDeletedBanner contactId={contactId} /> : null}
    </>
  );

  function renderSection(includeSidebar = false) {
    if (!contact) return null;
    switch (activeSection) {
      case 'summary':
        return (
          <div className="space-y-4">
            <ContactCustomerSummary
              contact={contact}
              onViewPayments={() => setActiveSection('payments')}
              onViewCreditMemos={() =>
                void router.navigate({
                  to: '/dashboard/credit-memos',
                  search: { ...DEFAULT_CREDIT_MEMO_LIST_FILTERS, contactId },
                })
              }
              onViewInvoices={() => setActiveSection('invoices')}
              onViewJobs={() => setActiveSection('jobs')}
              onEmailCompose={openEmailCompose}
              onSmsCompose={openSmsCompose}
            />
            {includeSidebar ? (
              <ContactInfoSidebar
                contact={contact}
                tagIds={tagIds}
                onTagIdsChange={handleTagIdsChange}
                onViewMemberships={() => setActiveSection('memberships')}
                onViewPaymentMethods={() => setActiveSection('payments')}
                onEmailCompose={openEmailCompose}
                className="w-full"
              />
            ) : null}
          </div>
        );
      case 'locations':
        return (
          <div className="space-y-4">
            <ContactLocationsPane contactId={contactId} />
            {/* Tax exemption and a pinned tax group belong to whoever is
                billed. A site contact is not billed — they are a person to
                meet at a place the customer owns — so the certificate,
                the reason and the pin are all questions for the customer. */}
            {contact.isSiteContactOnly !== true ? (
              <ContactTaxExemptionPane contactId={contactId} />
            ) : null}
          </div>
        );
      case 'communication':
        return <ContactCommunicationTimeline contactId={contactId} />;
      case 'estimates':
        return <ContactWorkTable contactId={contactId} kind="estimates" />;
      case 'jobs':
        return <ContactWorkTable contactId={contactId} kind="jobs" />;
      case 'projects':
        return <ContactProjectsPane contactId={contactId} />;
      case 'bill-to':
        return <ContactBillToSection contactId={contactId} currentContact={contact} />;
      case 'invoices':
        return (
          <ContactBillingTable
            contactId={contactId}
            onRecordPayment={() => openPaymentsAction('collect-payment')}
          />
        );
      case 'payments':
        return (
          <div className="space-y-6">
            <ContactPaymentMethodsSection
              contactId={contactId}
              contactName={displayName}
              primaryPhone={primaryPhone}
              primaryEmail={primaryEmail}
              externalAction={paymentsAction}
              externalActionToken={paymentsActionToken}
              onPaymentMethodCreated={completePaymentRetentionOffer}
            />
            {/* Collected payments (cash/check/card-on-file) for this contact.
                Previously only rendered under the Invoices/billing tab, so a
                manual collection looked like it "didn't reflect" on the
                Payments tab where the collect button lives. */}
            <ContactPaymentsSection contactId={contactId} />
            <ContactFinancialActivitySection contactId={contactId} />
          </div>
        );
      case 'receipts':
        return <ContactReceiptsSection contactId={contactId} />;
      case 'memberships':
        return <ContactMembershipsSection contact={contact} />;
      case 'equipment':
        return <ContactEquipmentPane contactId={contactId} />;
      case 'media':
        return <ContactMediaPane contactId={contactId} />;
      case 'notes':
        return <ContactNotesDocsPane contactId={contactId} />;
      default:
        return null;
    }
  }

  const deleteDialog = (
    <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete contact?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. {displayName} will be permanently deleted.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button variant="outline" onClick={handleDelete} disabled={deleteMutation.isPending}>
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (!isDesktop && contact && !query.isLoading) {
    return (
      <>
        {contact.isDeleted ? <ContactDeletedBanner contactId={contactId} /> : null}
        <ContactDetailMobile
          contact={contact}
          displayName={displayName}
          initials={initials}
          billToSummary={billToSummary}
          activeSection={activeSection}
          onSelectSection={setActiveSection}
          counts={{
            jobs: summaryQuery.data?.totalJobs,
            estimates: estimatesCountQuery.data?.total,
            invoices: invoicesCountQuery.data?.total,
          }}
          section={renderSection(true)}
          onBack={goBack}
          onEdit={() =>
            void router.navigate({
              to: '/dashboard/contacts/$contactId/edit',
              params: { contactId },
            })
          }
          onDelete={() => setDeleteOpen(true)}
          onEmailCompose={() => openEmailCompose()}
          onOpenBillTo={() => setActiveSection('bill-to')}
          onOpenPaymentsAction={openPaymentsAction}
        />
        <ContactCommunicationDialog
          contactId={contactId}
          open={compose.open}
          onOpenChange={(open) => setCompose((current) => ({ ...current, open }))}
          channel={compose.channel}
          initialTo={compose.to}
        />
        {deleteDialog}
      </>
    );
  }

  return (
    <>
      <DetailPageLayout
        header={header}
        headerClassName="block bg-background p-0"
        contentClassName={
          query.isLoading || !contact
            ? 'min-w-0'
            : 'flex min-h-0 gap-0 space-y-0 overflow-hidden p-0'
        }
      >
        {query.isLoading ? (
          <div className="space-y-6 p-5">
            <Skeleton className="h-10 w-full" />
            {Array.from({ length: 3 }).map((_, index) => (
              <Card key={index}>
                <CardContent className="space-y-3 pt-6">
                  <Skeleton className="h-5 w-44" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-4/5" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : contact ? (
          <>
            <ContactSectionNav
              active={activeSection}
              onSelect={setActiveSection}
              isSiteContactOnly={contact.isSiteContactOnly === true}
              counts={{
                jobs: summaryQuery.data?.totalJobs,
                estimates: estimatesCountQuery.data?.total,
                invoices: invoicesCountQuery.data?.total,
              }}
            />
            <div className="min-w-0 flex-1 overflow-y-auto px-6 py-5">{renderSection()}</div>
            <ContactInfoSidebar
              contact={contact}
              tagIds={tagIds}
              onTagIdsChange={handleTagIdsChange}
              onViewMemberships={() => setActiveSection('memberships')}
              onViewPaymentMethods={() => setActiveSection('payments')}
              onEmailCompose={openEmailCompose}
            />
          </>
        ) : null}
      </DetailPageLayout>

      <ContactCommunicationDialog
        contactId={contactId}
        open={compose.open}
        onOpenChange={(open) => setCompose((current) => ({ ...current, open }))}
        channel={compose.channel}
        initialTo={compose.to}
      />
      {deleteDialog}
    </>
  );
}
